// QuickStudy — view completa do tira-dúvida rápido.
//
// Usada em dois lugares:
//   - Tab "Tira-dúvida" dentro de Estudando com IA (layout full)
//   - Pop-out window (?view=quickstudy-window) — mesmo componente
//
// O Composer e o Chat são exportados pra reuso no Card da home e no Dock.

import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import {
  Archive,
  ArchiveRestore,
  ArrowUp,
  Camera,
  ChevronLeft,
  ChevronRight,
  FileText,
  ImagePlus,
  Loader2,
  MessageCirclePlus,
  Pencil,
  Sparkles,
  Trash2,
  X,
  ExternalLink
} from "lucide-react";
import {
  quickStudyConversations,
  quickStudyActiveId,
  quickStudyMessages,
  quickStudyDraftText,
  quickStudyLoading,
  quickStudySending,
  quickStudyError,
  quickStudyLimits,
  quickStudyListMode,
  loadQuickStudyConversations,
  loadQuickStudyLimits,
  openQuickStudyConversation,
  newQuickStudyConversation,
  deleteQuickStudyConversation,
  renameQuickStudyConversation,
  sendQuickStudyMessage,
  deleteQuickStudyMessage,
  compressImageForQuickStudy,
  setQuickStudyListMode,
  archiveQuickStudyConversation
} from "./quickStudyStore";
import { AiMarkdownBlock } from "./aiMarkdown";
import { openConfirm, openPrompt } from "./ConfirmPromptModal";
import { friendlyError } from "@app/ui/lib/friendly-error";
import type { QuickStudyImageInput } from "./api";
import { QuickStudyCropModal } from "./QuickStudyCropModal";
import { QuickStudyPdfPicker } from "./QuickStudyPdfPicker";

// ──────────────────────────────────────────────────────────────────────────
// Composer — caixa de envio com texto, dropzone de imagens, botão "Perguntar"
// ──────────────────────────────────────────────────────────────────────────

interface ComposerProps {
  conversationId: string | null;
  compact?: boolean;
  autoFocus?: boolean;
  onSent?: () => void;
  /** Quando true, cria a conversa automaticamente se ainda não tem ativa. */
  autoCreate?: boolean;
}

export function QuickStudyComposer({
  conversationId,
  compact = false,
  autoFocus = false,
  onSent,
  autoCreate = false
}: ComposerProps) {
  const [images, setImages] = useState<QuickStudyImageInput[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  // Imagem que está aguardando crop (vinda de câmera ou upload simples).
  // Quando setado, abre o QuickStudyCropModal por cima.
  const [pendingCrop, setPendingCrop] = useState<File | string | null>(null);
  // PDF que está aguardando seleção de páginas.
  const [pendingPdf, setPendingPdf] = useState<File | null>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const pdfInputRef = useRef<HTMLInputElement>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const limits = quickStudyLimits.value;

  useEffect(() => {
    if (autoFocus) taRef.current?.focus();
  }, [autoFocus]);

  // Auto-grow: a caixa começa pequena (1 linha) e cresce conforme o texto, até
  // um teto (acima dele, rola internamente). Antes era altura fixa (rows=4) e
  // comia o espaço da resposta mesmo vazia.
  //
  // "Grow-only": só aumenta pra caber o conteúdo, nunca encolhe sozinha. Assim
  // a alça nativa de redimensionar (resize-y) que o usuário arrastar pra cima
  // não briga com cada tecla digitada. Quando o campo esvazia (ex: após enviar)
  // a caixa volta ao mínimo.
  function autoGrowTextarea() {
    const ta = taRef.current;
    if (!ta) return;
    if (quickStudyDraftText.value.length === 0) {
      ta.style.height = "";
      ta.style.overflowY = "hidden";
      return;
    }
    const max = Math.round(window.innerHeight * (compact ? 0.28 : 0.35));
    if (ta.scrollHeight > ta.clientHeight) {
      const next = Math.min(ta.scrollHeight, max);
      ta.style.height = `${next}px`;
      ta.style.overflowY = ta.scrollHeight > max ? "auto" : "hidden";
    }
  }

  // Reage ao texto do signal (inclui o reset pra "" após enviar, que volta a
  // caixa pro tamanho mínimo) e roda uma vez na montagem.
  useEffect(() => {
    autoGrowTextarea();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quickStudyDraftText.value]);

  function appendImages(imgs: QuickStudyImageInput[]) {
    if (imgs.length === 0) return;
    const remaining = limits.maxImagesPerMessage - images.length;
    if (remaining <= 0) {
      setLocalError(
        `Você atingiu o limite de ${limits.maxImagesPerMessage} imagens.`
      );
      return;
    }
    const sliced = imgs.slice(0, remaining);
    const maxBytes = limits.maxImageSizeMb * 1024 * 1024;
    const ok: QuickStudyImageInput[] = [];
    for (const img of sliced) {
      if (img.sizeBytes > maxBytes) {
        setLocalError(
          `Imagem ficou com ${(img.sizeBytes / 1024 / 1024).toFixed(1)}MB (limite ${limits.maxImageSizeMb}MB). Tente recortar uma área menor.`
        );
        continue;
      }
      ok.push(img);
    }
    if (ok.length > 0) setImages([...images, ...ok]);
  }

  async function addFiles(files: FileList | File[]) {
    setLocalError(null);
    const remaining = limits.maxImagesPerMessage - images.length;
    if (remaining <= 0) {
      setLocalError(`Você atingiu o limite de ${limits.maxImagesPerMessage} imagens.`);
      return;
    }
    const arr = Array.from(files).slice(0, remaining);
    const compressed: QuickStudyImageInput[] = [];
    for (const f of arr) {
      if (!f.type.startsWith("image/")) {
        setLocalError(`"${f.name}" não é uma imagem.`);
        continue;
      }
      try {
        const c = await compressImageForQuickStudy(f);
        const maxBytes = limits.maxImageSizeMb * 1024 * 1024;
        if (c.sizeBytes > maxBytes) {
          setLocalError(
            `"${f.name}" ficou com ${(c.sizeBytes / 1024 / 1024).toFixed(1)}MB após compressão (limite ${limits.maxImageSizeMb}MB). Use uma versão menor.`
          );
          continue;
        }
        compressed.push(c);
      } catch (e) {
        setLocalError(`Falha ao processar "${f.name}": ${friendlyError(e)}`);
      }
    }
    if (compressed.length > 0) setImages([...images, ...compressed]);
  }

  /** Câmera: abre direto a captura, depois manda pro crop (recortar área da
   *  dúvida antes de mandar) — fluxo otimizado pra mobile. */
  function handleCameraSelect(files: FileList | null) {
    if (!files || files.length === 0) return;
    const f = files[0];
    if (!f.type.startsWith("image/")) {
      setLocalError("Arquivo da câmera não é uma imagem.");
      return;
    }
    setPendingCrop(f);
  }

  /** PDF: abre o picker de páginas. */
  function handlePdfSelect(files: FileList | null) {
    if (!files || files.length === 0) return;
    const f = files[0];
    if (f.type !== "application/pdf" && !f.name.toLowerCase().endsWith(".pdf")) {
      setLocalError("Selecione um arquivo PDF.");
      return;
    }
    // Cap de 20MB pra evitar PDFs absurdos.
    if (f.size > 20 * 1024 * 1024) {
      setLocalError("PDF muito grande (máx 20MB). Recorte as páginas que interessam antes.");
      return;
    }
    setPendingPdf(f);
  }

  function onDrop(ev: DragEvent) {
    ev.preventDefault();
    setDragOver(false);
    if (ev.dataTransfer?.files?.length) void addFiles(ev.dataTransfer.files);
  }

  function onPaste(ev: ClipboardEvent) {
    const items = ev.clipboardData?.items;
    if (!items) return;
    const files: File[] = [];
    for (const it of Array.from(items)) {
      if (it.kind === "file") {
        const f = it.getAsFile();
        if (f && f.type.startsWith("image/")) files.push(f);
      }
    }
    if (files.length) {
      ev.preventDefault();
      void addFiles(files);
    }
  }

  function removeImageAt(i: number) {
    setImages(images.filter((_, idx) => idx !== i));
  }

  async function handleSend() {
    const text = quickStudyDraftText.value.trim();
    if (!text && images.length === 0) return;
    let activeId = conversationId;
    if (!activeId && autoCreate) {
      const conv = await newQuickStudyConversation();
      if (!conv) return;
      activeId = conv.id;
    }
    if (!activeId) return;
    const r = await sendQuickStudyMessage(activeId, text, images);
    if (r.ok) {
      setImages([]);
      onSent?.();
    }
  }

  const disabled = !limits.enabled;
  const canSend =
    !disabled &&
    !quickStudySending.value &&
    (quickStudyDraftText.value.trim().length > 0 || images.length > 0);

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      onPaste={onPaste}
      class={`flex flex-col gap-2 rounded-lg border bg-card p-3 transition-colors ${
        dragOver
          ? "border-violet-500 bg-violet-500/5"
          : "border-border"
      }`}
    >
      {images.length > 0 && (
        <div class="flex flex-wrap gap-2">
          {images.map((img, i) => (
            <div
              key={i}
              class="group relative h-16 w-16 overflow-hidden rounded-md border border-border"
            >
              <img
                src={`data:${img.mimeType};base64,${img.base64}`}
                alt={`Imagem ${i + 1}`}
                class="h-full w-full object-cover"
              />
              <button
                type="button"
                onClick={() => removeImageAt(i)}
                aria-label="Remover imagem"
                title="Remover imagem"
                class="absolute right-0.5 top-0.5 rounded-full bg-black/70 p-0.5 text-white opacity-0 transition-opacity group-hover:opacity-100"
              >
                <X className="size-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      <textarea
        ref={taRef}
        value={quickStudyDraftText.value}
        onInput={(e) => {
          quickStudyDraftText.value = (e.target as HTMLTextAreaElement).value;
          autoGrowTextarea();
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            void handleSend();
          }
        }}
        placeholder={
          disabled
            ? "Tira-dúvida temporariamente desativado pelo administrador."
            : "Pergunte qualquer dúvida de estudo... (cole imagem com Ctrl+V ou arraste pra dentro)"
        }
        disabled={disabled}
        rows={1}
        class="block max-h-[35vh] min-h-[2.25rem] w-full resize-y rounded-md bg-transparent text-sm leading-relaxed outline-none placeholder:text-muted-foreground disabled:opacity-50"
      />

      {(localError || quickStudyError.value) && (
        <div class="text-[11px] text-destructive">
          {localError ?? quickStudyError.value}
        </div>
      )}

      <div class="flex items-center justify-between gap-2">
        <div class="flex items-center gap-1">
          {/* Câmera (mobile-first) — captura direto, depois crop */}
          <button
            type="button"
            disabled={disabled || images.length >= limits.maxImagesPerMessage}
            onClick={() => cameraInputRef.current?.click()}
            class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-[11px] font-medium text-muted-foreground hover:border-violet-500 hover:text-violet-600 disabled:cursor-not-allowed disabled:opacity-30"
            title="Tirar foto agora"
            aria-label="Tirar foto"
          >
            <Camera className="size-3.5" />
            <span class="hidden sm:inline">Foto</span>
          </button>
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            class="hidden"
            aria-label="Capturar foto pela câmera"
            title="Capturar foto pela câmera"
            onChange={(e) => {
              const files = (e.target as HTMLInputElement).files;
              handleCameraSelect(files);
              (e.target as HTMLInputElement).value = "";
            }}
          />

          {/* Galeria — múltiplas imagens, sem crop por padrão (mais rápido) */}
          <button
            type="button"
            disabled={disabled || images.length >= limits.maxImagesPerMessage}
            onClick={() => galleryInputRef.current?.click()}
            class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-[11px] font-medium text-muted-foreground hover:border-violet-500 hover:text-violet-600 disabled:cursor-not-allowed disabled:opacity-30"
            title="Anexar imagens (também: cole com Ctrl+V ou arraste pra cima)"
            aria-label="Anexar imagens"
          >
            <ImagePlus className="size-3.5" />
            {images.length}/{limits.maxImagesPerMessage}
          </button>
          <input
            ref={galleryInputRef}
            type="file"
            accept="image/*"
            multiple
            class="hidden"
            aria-label="Selecionar imagens"
            title="Selecionar imagens"
            onChange={(e) => {
              const files = (e.target as HTMLInputElement).files;
              if (files) void addFiles(files);
              (e.target as HTMLInputElement).value = "";
            }}
          />

          {/* PDF — abre picker de páginas */}
          <button
            type="button"
            disabled={disabled || images.length >= limits.maxImagesPerMessage}
            onClick={() => pdfInputRef.current?.click()}
            class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-[11px] font-medium text-muted-foreground hover:border-violet-500 hover:text-violet-600 disabled:cursor-not-allowed disabled:opacity-30"
            title="Anexar PDF (escolha páginas)"
            aria-label="Anexar PDF"
          >
            <FileText className="size-3.5" />
            <span class="hidden sm:inline">PDF</span>
          </button>
          <input
            ref={pdfInputRef}
            type="file"
            accept="application/pdf"
            class="hidden"
            aria-label="Selecionar PDF"
            title="Selecionar PDF"
            onChange={(e) => {
              const files = (e.target as HTMLInputElement).files;
              handlePdfSelect(files);
              (e.target as HTMLInputElement).value = "";
            }}
          />

          <span class="ml-1 hidden text-[10px] text-muted-foreground sm:inline">
            Ctrl+Enter envia
          </span>
        </div>
        <button
          type="button"
          disabled={!canSend}
          onClick={handleSend}
          class="inline-flex items-center gap-1.5 rounded-md bg-violet-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {quickStudySending.value ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : (
            <ArrowUp className="size-3.5" />
          )}
          {quickStudySending.value ? "Pensando..." : "Perguntar"}
        </button>
      </div>

      {/* Modal de crop (mostrado por cima quando há imagem aguardando) */}
      {pendingCrop && (
        <QuickStudyCropModal
          source={pendingCrop}
          onCancel={() => setPendingCrop(null)}
          onConfirm={(img) => {
            appendImages([img]);
            setPendingCrop(null);
          }}
        />
      )}

      {/* Picker de páginas de PDF */}
      {pendingPdf && (
        <QuickStudyPdfPicker
          file={pendingPdf}
          maxPages={Math.min(5, limits.maxImagesPerMessage - images.length)}
          onCancel={() => setPendingPdf(null)}
          onConfirm={(imgs) => {
            appendImages(imgs);
            setPendingPdf(null);
          }}
        />
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Chat — lista de mensagens com persona badge
// ──────────────────────────────────────────────────────────────────────────

interface ChatProps {
  compact?: boolean;
}

export function QuickStudyChat({ compact = false }: ChatProps) {
  const messages = quickStudyMessages.value;
  const activeId = quickStudyActiveId.value;
  const active = quickStudyConversations.value.find((c) => c.id === activeId);
  const scrollRef = useRef<HTMLDivElement>(null);
  // Lightbox in-app: lista de imagens da conversa atual + índice ativo.
  // Permite navegar prev/next com setas (do teclado e dos botões).
  const [lightbox, setLightbox] = useState<{
    images: Array<{ mimeType: string; base64: string }>;
    index: number;
  } | null>(null);

  useEffect(() => {
    // Auto-scroll para o fim
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, quickStudySending.value]);

  useEffect(() => {
    if (!lightbox) return;
    function onKey(e: KeyboardEvent) {
      if (!lightbox) return;
      if (e.key === "Escape") setLightbox(null);
      else if (e.key === "ArrowRight")
        setLightbox((s) =>
          s ? { ...s, index: (s.index + 1) % s.images.length } : s
        );
      else if (e.key === "ArrowLeft")
        setLightbox((s) =>
          s
            ? { ...s, index: (s.index - 1 + s.images.length) % s.images.length }
            : s
        );
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox]);

  async function handleDeleteMessage(messageId: string) {
    if (!activeId) return;
    const ok = await openConfirm({
      title: "Apagar mensagem?",
      message:
        "Essa mensagem some da conversa e do contexto que a IA usa. Não dá pra desfazer.",
      confirmLabel: "Apagar",
      danger: true
    });
    if (!ok) return;
    await deleteQuickStudyMessage(activeId, messageId);
  }

  if (!activeId) {
    return (
      <div class="flex flex-1 flex-col items-center justify-center gap-2 px-4 text-center text-sm text-muted-foreground">
        <Sparkles className="size-6 text-violet-500" />
        <div>Comece a perguntar ou selecione uma conversa.</div>
      </div>
    );
  }

  if (quickStudyLoading.value && messages.length === 0) {
    return (
      <div class="flex flex-1 items-center justify-center text-sm text-muted-foreground">
        <Loader2 className="mr-2 size-4 animate-spin" /> Carregando…
      </div>
    );
  }

  return (
    <>
    <div ref={scrollRef} class="min-h-0 flex-1 space-y-3 overflow-y-auto px-3 py-3">
      {active?.personaLabel && (
        <div class="flex items-center gap-2 rounded-md border border-violet-500/30 bg-violet-500/5 px-3 py-1.5 text-[11px]">
          <Sparkles className="size-3.5 text-violet-600" />
          <span class="font-semibold text-violet-700 dark:text-violet-300">
            Conversando com {active.personaLabel}
          </span>
        </div>
      )}
      {messages.map((m) => (
        <div
          key={m.id}
          class={`group flex items-start gap-1.5 ${
            m.role === "user" ? "flex-row-reverse" : "flex-row"
          }`}
        >
          <div
            class={
              m.role === "user"
                ? "max-w-[85%] rounded-lg bg-violet-600 px-3 py-2 text-sm text-white"
                : "max-w-[95%] rounded-lg border border-border bg-background px-3 py-2 text-sm"
            }
          >
            {m.images && m.images.length > 0 && (
              <div class="mb-2 flex flex-wrap gap-1.5">
                {m.images.map((img, idx) => (
                  <button
                    type="button"
                    key={idx}
                    onClick={() =>
                      setLightbox({
                        images: m.images!.map((i) => ({
                          mimeType: i.mimeType,
                          base64: i.base64
                        })),
                        index: idx
                      })
                    }
                    title="Clique para ampliar"
                    aria-label={`Ampliar imagem ${idx + 1}`}
                    class="block h-16 w-16 cursor-zoom-in overflow-hidden rounded-md border border-white/20 transition-opacity hover:opacity-80"
                  >
                    <img
                      src={`data:${img.mimeType};base64,${img.base64}`}
                      alt={`Imagem ${idx + 1}`}
                      class="h-full w-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
            {m.role === "assistant" ? (
              <AiMarkdownBlock text={m.content} />
            ) : (
              <div class="whitespace-pre-wrap">{m.content}</div>
            )}
            {m.refused && (
              <div class="mt-1 text-[10px] italic opacity-60">(resposta marcada pela validação anti-injection)</div>
            )}
          </div>
          {/* Apagar mensagem (enviada ou recebida). Aparece no hover. */}
          <button
            type="button"
            onClick={() => void handleDeleteMessage(m.id)}
            aria-label="Apagar mensagem"
            title="Apagar mensagem"
            class="mt-1 shrink-0 rounded p-1 text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100"
          >
            <Trash2 className="size-3.5" />
          </button>
        </div>
      ))}
      {quickStudySending.value && (
        <div class="mr-auto max-w-[85%] rounded-lg border border-border bg-background px-3 py-2 text-sm">
          <div class="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="size-3.5 animate-spin" />
            Pensando…
          </div>
        </div>
      )}
    </div>
    {lightbox && (
      <ImageLightbox
        images={lightbox.images}
        index={lightbox.index}
        onClose={() => setLightbox(null)}
        onNavigate={(dir) =>
          setLightbox((s) =>
            s
              ? {
                  ...s,
                  index:
                    dir === "next"
                      ? (s.index + 1) % s.images.length
                      : (s.index - 1 + s.images.length) % s.images.length
                }
              : s
          )
        }
      />
    )}
    </>
  );
}

function ImageLightbox({
  images,
  index,
  onClose,
  onNavigate
}: {
  images: Array<{ mimeType: string; base64: string }>;
  index: number;
  onClose: () => void;
  onNavigate: (dir: "prev" | "next") => void;
}) {
  const img = images[index];
  if (!img) return null;
  const hasMany = images.length > 1;
  return (
    <div
      class="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Fechar"
        title="Fechar (ESC)"
        class="absolute right-3 top-3 rounded-full bg-black/60 p-2 text-white hover:bg-black/80"
      >
        <X className="size-5" />
      </button>
      {hasMany && (
        <>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onNavigate("prev");
            }}
            aria-label="Imagem anterior"
            title="Anterior (←)"
            class="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-black/60 p-2 text-white hover:bg-black/80"
          >
            <ChevronLeft className="size-6" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onNavigate("next");
            }}
            aria-label="Próxima imagem"
            title="Próxima (→)"
            class="absolute right-14 top-1/2 -translate-y-1/2 rounded-full bg-black/60 p-2 text-white hover:bg-black/80"
          >
            <ChevronRight className="size-6" />
          </button>
        </>
      )}
      <img
        src={`data:${img.mimeType};base64,${img.base64}`}
        alt={`Imagem ${index + 1}`}
        onClick={(e) => e.stopPropagation()}
        class="max-h-[92vh] max-w-[92vw] cursor-default rounded-md shadow-2xl"
      />
      {hasMany && (
        <div class="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-3 py-1 text-xs font-semibold text-white">
          {index + 1} / {images.length}
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// View — sidebar de conversas + chat ativo + composer
// Usada em Estudando com IA (tab) E no pop-out window.
// ──────────────────────────────────────────────────────────────────────────

interface QuickStudyViewProps {
  /** Se true, mostra botão "Abrir em janela" no header. */
  showPopOut?: boolean;
}

export function QuickStudyView({ showPopOut = true }: QuickStudyViewProps) {
  useEffect(() => {
    void loadQuickStudyLimits();
    void loadQuickStudyConversations();
  }, []);

  const convs = quickStudyConversations.value;
  const activeId = quickStudyActiveId.value;
  const active = convs.find((c) => c.id === activeId);
  const listMode = quickStudyListMode.value;
  // No mobile a sidebar fica em drawer (escondida por padrão). Aluno abre via
  // botão "Conversas" no header.
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  async function handleNew() {
    // Sempre cria em "active". Se estava vendo arquivadas, troca pra ativa.
    if (listMode === "archived") setQuickStudyListMode("active");
    await newQuickStudyConversation();
    setMobileSidebarOpen(false);
  }

  function pickConversation(id: string) {
    void openQuickStudyConversation(id);
    setMobileSidebarOpen(false);
  }

  async function handleDelete(id: string) {
    const ok = await openConfirm({
      title: "Excluir conversa?",
      message: "Todas as mensagens dessa conversa serão removidas.",
      confirmLabel: "Excluir",
      danger: true
    });
    if (!ok) return;
    await deleteQuickStudyConversation(id);
  }

  async function handleRename(id: string, currentTitle: string) {
    const v = await openPrompt({
      title: "Renomear conversa",
      message: "Novo título da conversa",
      placeholder: "Nome curto",
      defaultValue: currentTitle
    });
    if (v && v.trim()) await renameQuickStudyConversation(id, v.trim());
  }

  function handlePopOut() {
    const url = activeId
      ? `?view=quickstudy-window&convId=${encodeURIComponent(activeId)}`
      : "?view=quickstudy-window";
    window.open(
      url,
      `quickstudy-${activeId ?? "new"}`,
      "width=560,height=820,resizable=yes,scrollbars=yes"
    );
  }

  return (
    // Altura FIXA — antes era min-height, então o chat crescia empurrando a
    // página inteira pra baixo e o composer sumia do viewport. Agora o card
    // tem altura limitada ao viewport e o scroll vive DENTRO do chat.
    // [[bug-2026-05-28: tira-dúvida scrolla página inteira]]
    //
    // Math do subtract (2026-05-29 Karol):
    //   Mobile  : topbar(3rem) + main py-6(3rem) + tabs(1.75rem) + gap(1rem) ≈ 9rem
    //   Desktop : topbar(3rem) + main py-8(4rem) + tabs(1.75rem) + gap(1rem)
    //             + dev footer(2.5rem) ≈ 12.25rem
    // Subtract com folga pequena pra device-pixel/borders.
    <div class="flex h-[calc(100dvh-10rem)] min-h-[28rem] flex-col gap-3 md:h-[calc(100dvh-13rem)]">
      <div class="flex items-center justify-between gap-3">
        <div class="flex min-w-0 items-center gap-2">
          {/* Botão "Conversas" no mobile abre o drawer */}
          <button
            type="button"
            onClick={() => setMobileSidebarOpen(true)}
            class="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2 py-1 text-[11px] font-medium md:hidden"
            aria-label="Abrir lista de conversas"
            title="Conversas"
          >
            <MessageCirclePlus className="size-3.5" />
            Conversas
          </button>
          <div class="min-w-0">
            <h2 class="flex items-center gap-2 text-base font-bold">
              <Sparkles className="size-4 text-violet-600" />
              <span class="truncate">{active?.title ?? "Tira-dúvida rápido"}</span>
            </h2>
            <p class="hidden text-xs text-muted-foreground sm:block">
              Pergunte sobre conteúdo de estudo. Anexe foto, PDF ou print.
            </p>
          </div>
        </div>
        <div class="flex shrink-0 items-center gap-2">
          {showPopOut && (
            <button
              type="button"
              onClick={handlePopOut}
              class="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2 py-1 text-[11px] font-medium hover:bg-muted"
              title="Abrir em janela separada pra mais foco"
            >
              <ExternalLink className="size-3.5" />
              <span class="hidden sm:inline">Janela</span>
            </button>
          )}
        </div>
      </div>

      <div class="grid min-h-0 flex-1 grid-cols-1 gap-3 md:grid-cols-[280px_1fr]">
        {/* Sidebar de conversas — drawer no mobile, fixo no desktop */}
        {mobileSidebarOpen && (
          <div
            class="fixed inset-0 z-40 bg-black/60 md:hidden"
            onClick={() => setMobileSidebarOpen(false)}
          />
        )}
        <aside
          class={`flex flex-col gap-2 rounded-md border border-border bg-card p-2 md:bg-card/40 ${
            mobileSidebarOpen
              ? "fixed inset-y-4 left-4 z-50 w-[min(280px,calc(100vw-2rem))] shadow-2xl md:static md:inset-auto md:w-auto md:shadow-none"
              : "hidden md:flex"
          }`}
        >
          <button
            type="button"
            onClick={handleNew}
            class="inline-flex items-center gap-1.5 rounded-md border border-violet-500/40 bg-violet-500/5 px-2 py-1.5 text-xs font-semibold text-violet-700 hover:bg-violet-500/10 dark:text-violet-300"
          >
            <MessageCirclePlus className="size-3.5" />
            Nova conversa
          </button>
          {/* Toggle Ativas | Arquivadas */}
          <div class="inline-flex gap-0.5 rounded-md border border-border bg-background p-0.5">
            <button
              type="button"
              onClick={() => setQuickStudyListMode("active")}
              class={`flex-1 rounded-md px-2 py-1 text-[10px] font-bold transition-colors ${
                listMode === "active"
                  ? "bg-violet-600 text-white"
                  : "text-muted-foreground hover:bg-muted"
              }`}
            >
              Ativas
            </button>
            <button
              type="button"
              onClick={() => setQuickStudyListMode("archived")}
              class={`flex-1 rounded-md px-2 py-1 text-[10px] font-bold transition-colors ${
                listMode === "archived"
                  ? "bg-violet-600 text-white"
                  : "text-muted-foreground hover:bg-muted"
              }`}
            >
              Arquivadas
            </button>
          </div>
          <div class="-mr-1 flex-1 overflow-y-auto pr-1">
            {convs.length === 0 && (
              <div class="px-1 py-4 text-center text-[11px] text-muted-foreground">
                {listMode === "archived"
                  ? "Nenhuma conversa arquivada."
                  : "Nenhuma conversa ainda."}
              </div>
            )}
            {convs.map((c) => (
              <div
                key={c.id}
                class={`group flex items-center gap-1 rounded-md px-2 py-1.5 text-xs transition-colors ${
                  c.id === activeId ? "bg-violet-500/10 text-violet-700 dark:text-violet-300" : "hover:bg-muted"
                }`}
              >
                <button
                  type="button"
                  onClick={() => pickConversation(c.id)}
                  class="min-w-0 flex-1 truncate text-left"
                  title={c.title}
                >
                  {c.title}
                </button>
                {listMode === "active" ? (
                  <>
                    <button
                      type="button"
                      onClick={() => void handleRename(c.id, c.title)}
                      aria-label="Renomear"
                      title="Renomear"
                      class="opacity-0 transition-opacity hover:text-violet-600 group-hover:opacity-100"
                    >
                      <Pencil className="size-3" />
                    </button>
                    <button
                      type="button"
                      onClick={() => void archiveQuickStudyConversation(c.id, true)}
                      aria-label="Arquivar"
                      title="Arquivar"
                      class="opacity-0 transition-opacity hover:text-amber-600 group-hover:opacity-100"
                    >
                      <Archive className="size-3" />
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleDelete(c.id)}
                      aria-label="Excluir"
                      title="Excluir"
                      class="opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100"
                    >
                      <Trash2 className="size-3" />
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => void archiveQuickStudyConversation(c.id, false)}
                      aria-label="Restaurar"
                      title="Restaurar"
                      class="opacity-0 transition-opacity hover:text-emerald-600 group-hover:opacity-100"
                    >
                      <ArchiveRestore className="size-3" />
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleDelete(c.id)}
                      aria-label="Excluir permanentemente"
                      title="Excluir permanentemente"
                      class="opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100"
                    >
                      <Trash2 className="size-3" />
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        </aside>

        {/* Chat + composer.
            min-h-0 garante que esse grid item respeite o flex-1 do parent e
            permita o overflow interno do <QuickStudyChat /> rolar; sem isso o
            grid item cresce até caber todo o conteúdo (= bug do scroll só na
            página inteira). Composer fica fixo no rodapé via shrink-0. */}
        <section class="flex min-h-0 flex-col gap-2 rounded-md border border-border bg-card/40">
          <QuickStudyChat />
          <div class="shrink-0 border-t border-border p-2">
            <QuickStudyComposer
              conversationId={activeId}
              autoCreate
              autoFocus={!active}
            />
          </div>
        </section>
      </div>
    </div>
  );
}
